import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  ResponseType as AxiosResponseType,
} from 'axios';

import { redirectIfAdminRequiresCrew } from 'utils/adminHost';
import { isAccessExpired, reloadForAccessExpiry } from 'utils/auth';
import { IS_ADMIN_HOST } from 'utils/envVars';

import Constants from '../constants';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong.';

type RetriedRequestConfig = {
  /** Flag that indicates a request is being retried after a 401 and token refresh */
  psRetry?: boolean;
} & AxiosRequestConfig;

interface Options {
  body?: any;
  throwErrorAsString?: boolean;
  returnNestedResponseData?: boolean;
  withCredentials?: boolean;
  responseType?: AxiosResponseType;
  /**
   * `timeout` specifies the number of milliseconds before the request times out.
   * If the request takes longer than `timeout`, the request will be aborted.
   */
  timeout?: number;
}

const DEFAULT_OPTIONS: Options = {
  throwErrorAsString: true,
  returnNestedResponseData: true,
};

const { internalApiHost, globalOffersApiHost, cashbackApiHost } = Constants;

// Admin-host calls are same-origin through Cloudflare Access, which answers
// XHR-flagged requests with 401 instead of a login redirect. Merchant hosts
// call internal-api cross-origin, where this header would force preflights.
const internalApiAxiosClient = axios.create({
  baseURL: internalApiHost,
  headers: IS_ADMIN_HOST ? { 'X-Requested-With': 'XMLHttpRequest' } : {},
});

const offersAxiosClient = axios.create({
  baseURL: globalOffersApiHost,
});

const cashbackAxiosClient = axios.create({
  baseURL: cashbackApiHost,
});

type AxiosInterceptorIds = {
  request: number;
  responseAuth: number;
  responseApp: number;
};

let internalApiAxiosInterceptors: AxiosInterceptorIds | null = null;
let offersAxiosInterceptors: AxiosInterceptorIds | null = null;
let cashbackAxiosInterceptors: AxiosInterceptorIds | null = null;

// Track ongoing token refresh to avoid multiple concurrent refresh attempts
let refreshRequest: {
  token: string | null;
  promise: Promise<string | null>;
} | null = null;

export const extractErrorMessage = (error: Error | AxiosError) => {
  if (typeof error === 'string') return error;

  if (axios.isAxiosError(error))
    return (
      error.response?.data?.error ||
      error.response?.data?.error_message ||
      (error.response?.data?.errors?.length &&
        error.response?.data?.errors[0]) ||
      error.response?.data?.message ||
      error.message ||
      error
    );

  return error?.message || error;
};

const attachInterceptors = (
  client: AxiosInstance,
  axiosInterceptors: AxiosInterceptorIds | null,
  accessToken: string | null,
  refreshAccessToken?: (options?: {
    rejectedAccessToken?: string;
  }) => Promise<string | null>,
): AxiosInterceptorIds => {
  // When our access token changes, then our axios interceptors need to be recreated to use the
  // new access token. So first we eject all the existing interceptors before replacing them
  // with the new ones.
  if (axiosInterceptors) {
    client.interceptors.request.eject(axiosInterceptors.request);
    client.interceptors.response.eject(axiosInterceptors.responseAuth);
    client.interceptors.response.eject(axiosInterceptors.responseApp);
  }

  const accessTokenRequestInterceptor = client.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      if (accessToken && config.headers && !config.headers.Authorization) {
        // eslint-disable-next-line no-param-reassign
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
  );

  const accessTokenResponseInterceptor = client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetriedRequestConfig;

      if (client === internalApiAxiosClient) {
        redirectIfAdminRequiresCrew(
          error.response?.status,
          error.response?.data,
        );
      }

      // Only the internal client is proxied by the admin edge; offers and
      // cashback are cross-origin and never carry its marker.
      if (
        client === internalApiAxiosClient &&
        isAccessExpired(error.response?.status, error.response?.headers) &&
        reloadForAccessExpiry() === 'reloading'
      ) {
        return Promise.reject(error);
      }

      // Check if this is a 401 error and we haven't already tried to refresh for this request
      if (
        error.response?.status === 401 &&
        !originalRequest.psRetry &&
        refreshAccessToken
      ) {
        originalRequest.psRetry = true;

        try {
          const rejectedToken =
            typeof originalRequest.headers?.Authorization === 'string'
              ? originalRequest.headers.Authorization.replace(/^Bearer /, '')
              : accessToken;
          if (!refreshRequest || refreshRequest.token !== rejectedToken) {
            refreshRequest = {
              token: rejectedToken,
              promise: refreshAccessToken({
                rejectedAccessToken: rejectedToken || undefined,
              }),
            };
          }
          const request = refreshRequest;
          let newToken: string | null;
          try {
            newToken = await request.promise;
          } finally {
            if (refreshRequest === request) refreshRequest = null;
          }

          if (newToken) {
            // Ensure headers exists, then retry the original request with the new token
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${newToken}`,
            };
            return client(originalRequest);
          }

          // If refresh failed, reject the original request
          return Promise.reject(error);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  const responseInterceptor = client.interceptors.response.use(
    (data) => {
      if (data.data?.success === false) {
        throw new Error(
          data.data.error || data.data.message || DEFAULT_ERROR_MESSAGE,
        );
      }

      return data;
    },
    async (error) => {
      throw error;
    },
  );

  return {
    request: accessTokenRequestInterceptor,
    responseAuth: accessTokenResponseInterceptor,
    responseApp: responseInterceptor,
  };
};

export function initializeAxiosClients(
  accessToken: string | null,
  refreshAccessToken?: (options?: {
    rejectedAccessToken?: string;
  }) => Promise<string | null>,
) {
  internalApiAxiosInterceptors = attachInterceptors(
    internalApiAxiosClient,
    internalApiAxiosInterceptors,
    accessToken,
    refreshAccessToken,
  );

  offersAxiosInterceptors = attachInterceptors(
    offersAxiosClient,
    offersAxiosInterceptors,
    accessToken,
    refreshAccessToken,
  );

  cashbackAxiosInterceptors = attachInterceptors(
    cashbackAxiosClient,
    cashbackAxiosInterceptors,
    accessToken,
    refreshAccessToken,
  );
}

const createApi = (client: AxiosInstance) => {
  return {
    get: async <TResponse = any>(
      url: string,
      options?: Options,
    ): Promise<TResponse> => {
      const {
        returnNestedResponseData,
        throwErrorAsString,
        responseType,
        timeout,
      } = {
        ...DEFAULT_OPTIONS,
        ...options,
      };

      try {
        const response = await client.get(url, {
          responseType,
          timeout,
        });
        return returnNestedResponseData ? response.data : response;
      } catch (error: any) {
        if (throwErrorAsString) throw extractErrorMessage(error);
        throw error;
      }
    },
    post: async <TResponse = any>(
      url: string,
      body = {},
      options?: Options,
    ): Promise<TResponse> => {
      const { returnNestedResponseData, throwErrorAsString, withCredentials } =
        {
          ...DEFAULT_OPTIONS,
          ...options,
        };

      const config = {
        ...(withCredentials ? { withCredentials: true } : {}),
      };

      try {
        const response = await client.post(url, body, config);
        return returnNestedResponseData ? response.data : response;
      } catch (error: any) {
        if (throwErrorAsString) throw extractErrorMessage(error);
        throw error;
      }
    },
    put: async <TResponse = any>(
      url: string,
      body = {},
      options?: Options,
    ): Promise<TResponse> => {
      const { returnNestedResponseData, throwErrorAsString } = {
        ...DEFAULT_OPTIONS,
        ...options,
      };

      try {
        const response = await client.put(url, body);
        return returnNestedResponseData ? response.data : response;
      } catch (error: any) {
        if (throwErrorAsString) throw extractErrorMessage(error);
        throw error;
      }
    },
    patch: async <TResponse = any>(
      url: string,
      body = {},
      options?: Options,
    ): Promise<TResponse> => {
      const { returnNestedResponseData, throwErrorAsString } = {
        ...DEFAULT_OPTIONS,
        ...options,
      };

      try {
        const response = await client.patch(url, body);
        return returnNestedResponseData ? response.data : response;
      } catch (error: any) {
        if (throwErrorAsString) throw extractErrorMessage(error);
        throw error;
      }
    },
    delete: async <TResponse = any>(
      url: string,
      options?: Options,
    ): Promise<TResponse> => {
      const { returnNestedResponseData, throwErrorAsString, body } = {
        ...DEFAULT_OPTIONS,
        ...options,
      };

      try {
        const response = await client.delete(url, { data: body });
        return returnNestedResponseData ? response.data : response;
      } catch (error: any) {
        if (throwErrorAsString) throw extractErrorMessage(error);
        throw error;
      }
    },
    postFormData: async <TResponse = any>(
      url: string,
      formData: FormData,
      options?: Options,
    ): Promise<TResponse> => {
      const { returnNestedResponseData, throwErrorAsString } = {
        ...DEFAULT_OPTIONS,
        ...options,
      };

      try {
        const response = await client.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        return returnNestedResponseData ? response.data : response;
      } catch (error: any) {
        if (throwErrorAsString) throw extractErrorMessage(error);
        throw error;
      }
    },
  };
};

export const api = createApi(internalApiAxiosClient);

export const offersApi = createApi(offersAxiosClient);

export const cashbackApi = createApi(cashbackAxiosClient);
